import React, {useEffect} from 'react';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import {calculateApneaScores} from 'screens/InsomniaSurvey/utils/apneaScoring';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import Analytics from 'utils/Analytics';

/**
 * Silent OSA scoring step for the SleepioRx flow.
 *
 * Computes + persists the apnea scores that feed the intake `osaRisk` payload,
 * fires the existing OSA-screener analytics, then auto-advances WITHOUT rendering
 * a result. We intentionally do NOT show the user the clinical "Sleep apnea
 * screener" result card (`ApneaRiskInterstitial`, kept but no longer in this flow):
 * the OSA signal is informational context for the reviewing clinician, not a
 * user-facing result that could alarm or distract. See RET-2554 (QA rework) / RET-2558.
 */
const ApneaScoringInterstitial: React.FC = () => {
  useEffect(() => {
    const scores = calculateApneaScores(surveyInstance.data);
    surveyInstance.setValue('bmi', scores.bmi);
    surveyInstance.setValue('apnealink_score', scores.apnealinkScore);
    surveyInstance.setValue('au_osa_score', scores.auScore);
    surveyInstance.setValue('qualified_resmed_apnealink', scores.qualifiedResmedApnealink);
    surveyInstance.setValue('qualified_au_osa', scores.qualifiedAuOsa);
    surveyInstance.setValue('qualified_osa_risk', scores.qualifiedOsaRisk);
    SurveyData.save(surveyInstance);

    Analytics.identify({
      web_apnealink_score: scores.apnealinkScore.toString(),
      web_au_osa_score: scores.auScore.toString(),
      web_qualified_osa_risk: scores.qualifiedOsaRisk.toString(),
    });
    Analytics.track('Clinic Web Intake OSA Screener Completed', {
      apnealink_score: scores.apnealinkScore,
      au_osa_score: scores.auScore,
      bmi: scores.bmi,
      qualified_resmed_apnealink: scores.qualifiedResmedApnealink,
      qualified_au_osa: scores.qualifiedAuOsa,
      qualified_osa_risk: scores.qualifiedOsaRisk,
    });

    const timer = window.setTimeout(() => surveyInstance.nextPage(), 50);
    return () => window.clearTimeout(timer);
  }, []);

  return null;
};

export default ApneaScoringInterstitial;
